import {useNavigate} from "react-router-dom"
import {Resource} from "../../resources/common"
import {CheckDeposit, CheckDepositStatus, CheckDepositVendors} from "../../resources/checkDeposits"
import {AllowedCheckDepositColumns, CheckDepositColumns} from "./CheckDeposit.types"
import {RejectCheckModal} from "./RejectCheckModal"
import {useModal} from "react-modal-hook"
import numeral from "numeral"
import React, {ReactElement} from "react"
import {ApproveCheckModal} from "./ApproveCheckModal"
import {ReturnCheckModal} from "./ReturnCheckModal"
import {CancelCheckModal} from "./CancelCheckModal"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import classNames from "classnames"
import {OrgName} from "../OrgName/OrgName"
import {kebabCase, startCase} from "lodash"
import moment from "moment"
import ReactTooltip from "react-tooltip"
import {DataTableActionButton, DataTableActionItem} from "../DataTable/DataTableActionButton"
import {checkDepositTooltipValue} from "./CheckDepositDetails"
import IdLink from "../IdLink/IdLink"
import {getButtonsToShow} from "./checkDepositActions"
import {getPermittedButtons} from "./checkDepositPermissions"
import {useIsOrgAdmin} from "../../services/auth"

interface CheckDepositRowProps {
	cd: CheckDeposit
	include?: Resource[]
	includedColumns: Array<AllowedCheckDepositColumns>
	onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void
	refresh: () => void
	selectedCheckDepositId?: string
}

export default function CheckDepositRow({
	cd,
	include,
	includedColumns,
	onClick,
	refresh,
	selectedCheckDepositId,
}: CheckDepositRowProps) {
	const navigate = useNavigate()
	const isOrgAdmin = useIsOrgAdmin()
	const id = cd.id
	const org = cd.relationships.org?.data.id
	const vendor = cd.attributes?.vendor
	const customerId = cd.relationships.customer?.data.id
	const accountId = cd.relationships.account.data.id
	const checkNumber = cd.attributes?.checkNumber
	const status = cd.attributes.status
	const depositDate = cd.attributes.createdAt
	const settlementDate = cd.attributes?.settlementDate
	const amount = cd.attributes.amount
	const reason = cd.attributes.reason
	const customer =
		customerId && include
			? include.find(
					(r) =>
						(r.type == "individualCustomer" || r.type == "businessCustomer" || r.type == "businessFBOCustomer") &&
						r.id == customerId.toString()
			  )
			: undefined
	let customerName: string | undefined = undefined

	const buttonsToShow = getButtonsToShow(status, vendor)
	const permittedButtons = getPermittedButtons(buttonsToShow, isOrgAdmin)

	const enableRejectAndApprove =
		status === CheckDepositStatus.PendingReview &&
		vendor !== CheckDepositVendors.Ensenta &&
		vendor !== CheckDepositVendors.Veryfi &&
		permittedButtons?.includes("Approve") &&
		permittedButtons?.includes("Reject")
	const enableReturn =
		(status === CheckDepositStatus.Clearing || status === CheckDepositStatus.Sent) &&
		permittedButtons?.includes("Return")
	const hasVeryfiCancel = permittedButtons?.includes("Cancel")

	const enableActions = !!(enableRejectAndApprove || enableReturn || hasVeryfiCancel)

	const [showRejectCheck, hideRejectCheck] = useModal(() => (
		<RejectCheckModal
			closeModal={hideRejectCheck}
			id={id}
			onSuccess={refresh}
			isVeryfi={vendor == CheckDepositVendors.Veryfi}
		/>
	))

	const [showApproveCheck, hideApproveCheck] = useModal(() => (
		<ApproveCheckModal closeModal={hideApproveCheck} id={id} onSuccess={refresh} />
	))

	const [showReturnCheck, hideReturnCheck] = useModal(() => (
		<ReturnCheckModal closeModal={hideReturnCheck} id={id} onSuccess={refresh} />
	))

	const [showCancelCheck, hideCancelCheck] = useModal(() => (
		<CancelCheckModal closeModal={hideCancelCheck} id={id} onSuccess={refresh} />
	))

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer" || customer.type == "businessFBOCustomer") {
			customerName = customer.attributes.name
		}
	}

	const contentColumns: Partial<Record<AllowedCheckDepositColumns, ReactElement>> = {}

	if (includedColumns.includes(CheckDepositColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell
				className={classNames(id == selectedCheckDepositId && "selected-data-table-id", "data-table-id-cell")}
			>
				<IdLink className="data-table-id" href={`/check-deposits/${id}`}>
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckDepositColumns.org) && org) {
		contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} />
	}

	if (includedColumns.includes(CheckDepositColumns.customer)) {
		contentColumns["Customer"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (customer) {
							navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
						}
					}}
				>
					{customerName}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckDepositColumns.accountId)) {
		contentColumns["Account"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (accountId) {
							navigate(`/accounts/${accountId}`)
						}
					}}
				>
					{accountId}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckDepositColumns.checkNumber)) {
		contentColumns["Check number"] = <DataTableCell>{checkNumber}</DataTableCell>
	}

	if (includedColumns.includes(CheckDepositColumns.status)) {
		contentColumns["Status"] = (
			<DataTableCell>
				<div className={classNames("check-deposit-status", kebabCase(status))}>{startCase(status)}</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckDepositColumns.depositDate)) {
		contentColumns["Deposit Date"] = <DataTableCell>{moment(depositDate).format("MM/DD/YYYY")}</DataTableCell>
	}

	if (includedColumns.includes(CheckDepositColumns.reason)) {
		contentColumns["Reason"] = <DataTableCell>{reason ? reason : "-"}</DataTableCell>
	}

	if (includedColumns.includes(CheckDepositColumns.vendor)) {
		contentColumns["Vendor"] = <DataTableCell>{vendor}</DataTableCell>
	}

	if (includedColumns.includes(CheckDepositColumns.expectedCompletionDate)) {
		let tooltipValue: string | undefined = undefined
		if (settlementDate) {
			tooltipValue = checkDepositTooltipValue
		}

		contentColumns["Expected Completion Date"] = (
			<DataTableCell>
				<span className="is-flex is-align-items-center ml-1">
					<span data-tip="React-tooltip" data-for={`check-deposit-expected-completion-time-tooltip-${id}`}>
						{settlementDate ? (
							moment(settlementDate).format("MM/DD/YYYY")
						) : (
							<span className="empty-date-placeholder"> - </span>
						)}
					</span>
					<ReactTooltip
						className="tooltip-info"
						id={`check-deposit-expected-completion-time-tooltip-${id}`}
						disable={!tooltipValue}
						effect="solid"
					>
						{tooltipValue}
					</ReactTooltip>
				</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckDepositColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>
				<span className="check-deposit-amount">{numeral(amount / 100).format("$0,0.00")}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckDepositColumns.actions)) {
		contentColumns["Actions"] = (
			<DataTableActionButton enableActions={enableActions}>
				{enableRejectAndApprove && (
					<>
						<DataTableActionItem
							title={"Reject check"}
							icon={"interface-delete-interface-essential"}
							onClick={() => showRejectCheck()}
						/>
						<DataTableActionItem
							title={"Approve check"}
							icon={"interface-validation-check--interface-essential"}
							onClick={() => showApproveCheck()}
						/>
					</>
				)}
				{hasVeryfiCancel && (
					<DataTableActionItem
						title={"Cancel check"}
						icon={"interface-delete-interface-essential"}
						onClick={() => showCancelCheck()}
					/>
				)}
				{enableReturn && (
					<DataTableActionItem
						title={"Mark as returned"}
						icon={"interface-validation-check--interface-essential"}
						onClick={() => showReturnCheck()}
					/>
				)}
			</DataTableActionButton>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return (
		<DataTableRow
			className={classNames(id === selectedCheckDepositId && "selected-data-table-row")}
			onClick={(e) => (onClick ? onClick(e) : null)}
		>
			{content}
		</DataTableRow>
	)
}
